import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFileUrl, apiGet, apiPost } from '../../lib/api';

/**
 * Ecranul e-Factura, dinspre client ([#240](https://github.com/bplugaru/efx-pcrm/issues/240)).
 *
 * Schemele sunt scrise aici, nu importate din `@pcrm/be/billing`: `apps/crm` e `scope:fe`.
 */

export const E_FACTURA_STATUSES = [
  'queued',
  'uploading',
  'processing',
  'accepted',
  'rejected',
  'unclear',
] as const;

const statusSchema = z.enum(E_FACTURA_STATUSES);

export type EFacturaStatus = z.infer<typeof statusSchema>;

/** Starea, cum o vede omul. `null` = factura n-a fost marcată niciodată. */
export const E_FACTURA_STATUS_LABELS: Readonly<Record<EFacturaStatus | 'none', string>> = {
  none: 'Nemarcată',
  queued: 'De trimis',
  uploading: 'Se trimite',
  processing: 'În prelucrare la ANAF',
  accepted: 'Acceptată',
  rejected: 'Respinsă',
  unclear: 'Neclară',
};

export const E_FACTURA_STATUS_COLORS: Readonly<
  Record<EFacturaStatus | 'none', 'grey' | 'blue' | 'green' | 'red' | 'orange'>
> = {
  none: 'grey',
  queued: 'blue',
  uploading: 'blue',
  processing: 'blue',
  accepted: 'green',
  rejected: 'red',
  unclear: 'orange',
};

/** Stările în care factura stă la coadă sau e pe drum: lista se reîncarcă singură. */
export const E_FACTURA_MOVING: readonly EFacturaStatus[] = ['queued', 'uploading', 'processing'];

/** Se poate marca „de trimis": nemarcată, respinsă (după corectură) sau neclară. */
export function canQueue(status: EFacturaStatus | null): boolean {
  return status === null || status === 'rejected' || status === 'unclear';
}

const attemptSchema = z.object({
  id: z.uuid(),
  status: statusSchema,
  queuedBy: z.string(),
  queuedAt: z.string(),
  environment: z.enum(['test', 'prod']).nullable(),
  uploadedAt: z.string().nullable(),
  uploadIndex: z.string().nullable(),
  checks: z.number(),
  checkedAt: z.string().nullable(),
  anafState: z.string().nullable(),
  finishedAt: z.string().nullable(),
  error: z.string().nullable(),
  failures: z.number(),
  nextAttemptAt: z.string().nullable(),
  hasResponse: z.boolean(),
});

export type EFacturaAttempt = z.infer<typeof attemptSchema>;

const rowSchema = z.object({
  invoiceId: z.uuid(),
  companyId: z.string(),
  companyName: z.string(),
  customerId: z.string(),
  buyerName: z.string(),
  series: z.string(),
  number: z.string(),
  issueDate: z.string(),
  currency: z.string(),
  payable: z.string(),
  status: statusSchema.nullable(),
  editable: z.boolean(),
  files: z.object({ xml: z.boolean(), pdf: z.boolean() }),
  latest: attemptSchema.nullable(),
  attempts: z.number(),
});

export type EFacturaRow = z.infer<typeof rowSchema>;

const countsSchema = z.object({
  none: z.number(),
  queued: z.number(),
  uploading: z.number(),
  processing: z.number(),
  accepted: z.number(),
  rejected: z.number(),
  unclear: z.number(),
});

export type EFacturaCounts = z.infer<typeof countsSchema>;

const viewSchema = z.object({
  rows: z.array(rowSchema),
  counts: countsSchema,
  truncated: z.boolean(),
  limit: z.number(),
  companies: z.array(z.object({ id: z.string(), name: z.string() })),
});

export type EFacturaView = z.infer<typeof viewSchema>;

const queueResultSchema = z.object({
  queued: z.number(),
  skipped: z.array(
    z.object({
      invoiceId: z.string(),
      label: z.string(),
      reason: z.string(),
      message: z.string(),
    }),
  ),
});

export type EFacturaQueueResult = z.infer<typeof queueResultSchema>;

export const E_FACTURA_QUERY_KEY = ['efactura'] as const;

export interface EFacturaFilter {
  readonly companyId?: string;
  readonly statuses?: readonly (EFacturaStatus | 'none')[];
  /** Data emiterii, inclusiv. */
  readonly from?: string;
  readonly to?: string;
}

export function eFacturaQueryOptions(filter: EFacturaFilter) {
  const params = new URLSearchParams();

  if (filter.companyId) {
    params.set('companyId', filter.companyId);
  }

  if (filter.statuses && filter.statuses.length > 0) {
    params.set('status', filter.statuses.join(','));
  }

  if (filter.from) {
    params.set('from', filter.from);
  }

  if (filter.to) {
    params.set('to', filter.to);
  }

  const query = params.toString();

  return queryOptions({
    queryKey: [...E_FACTURA_QUERY_KEY, 'list', filter],
    queryFn: () => apiGet(`/efactura${query ? `?${query}` : ''}`, viewSchema),
    // Cât e ceva pe drum, lista se reîncarcă: omul vrea să vadă factura trecând în „Acceptată".
    refetchInterval: (state) =>
      state.state.data?.rows.some((row) => row.status !== null && E_FACTURA_MOVING.includes(row.status))
        ? 10_000
        : false,
  });
}

export function eFacturaAttemptsQueryOptions(invoiceId: string) {
  return queryOptions({
    queryKey: [...E_FACTURA_QUERY_KEY, 'attempts', invoiceId],
    queryFn: () =>
      apiGet(
        `/efactura/invoices/${invoiceId}/attempts`,
        z.object({ attempts: z.array(attemptSchema) }),
      ).then((result) => result.attempts),
  });
}

export function queueEFactura(invoiceIds: readonly string[]): Promise<EFacturaQueueResult> {
  return apiPost('/efactura/queue', { invoiceIds }, queueResultSchema);
}

export function eFacturaResponseUrl(attemptId: string): string {
  return apiFileUrl(`/efactura/attempts/${attemptId}/response`);
}
